/**
 * KA-49 §4.3 — "TIẾN TRÌNH LỆNH" dưới khối Điều khiển độ sáng (theo mẫu `mau_van_hanh`): lệnh dim gần nhất của đối tượng đang chọn.
 *   Đèn ⇒ 4 bước của đèn. Tủ / tuyến (lệnh nhiều đèn) ⇒ bước 2–4 "x / y đèn đạt", bấm mở danh sách từng đèn.
 *   Luật 4 bước: MỘT hàm thuần `lib/tienTrinhLenh.ts`. Đang chờ ⇒ vàng + đếm giây. Dữ liệu chưa tải ⇒ không vẽ gì (không "chưa có lệnh").
 * 🔴 Câu cuối khối nói thẳng: báo nhận của host chỉ xác nhận "đã phát ra mesh".
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { lechGioMs } from '../../lib/giomaychu';
import { tenDen } from '../../lib/nemaNhan';
import {
  NHAN_KET_QUA, lenhGanNhatCuaDen, tienTrinhDen, tienTrinhNhieu, type Buoc, type LenhTienTrinh, type TienTrinhTu,
} from '../../lib/tienTrinhLenh';
import type { DenBanDo } from '../nema/LopDenNema';

export type ChonTienTrinh =
  | { loai: 'den'; den: DenBanDo }
  | { loai: 'tu'; id: string; ten: string }
  | { loai: 'tuyen'; tu: { id: string; name: string }[] };

/** Lệnh nhiều tủ phát "cùng lúc" (một cú bấm tuyến tạo một lệnh mỗi tủ) — cách nhau tối đa chừng này. */
const CUNG_LUC_MS = 15_000;

function useBayGio(chay: boolean): string {
  const [t, setT] = useState(() => Date.now());
  useEffect(() => { if (!chay) return; const h = window.setInterval(() => setT(Date.now()), 1000); return () => window.clearInterval(h); }, [chay]);
  return new Date(t + (lechGioMs() ?? 0)).toISOString();
}

const MAU: Record<Buoc['tt'], string> = { xong: 'xong', cho: 'cho', loi: 'loi', chua: 'chua' };

function DanhSachBuoc({ buoc }: { buoc: Buoc[] }) {
  return (
    <ol className="op-tien-trinh" data-o="cac-buoc">
      {buoc.map((b, i) => (
        <li key={b.so} data-buoc={b.so} data-tt={MAU[b.tt]} className={i === buoc.length - 1 ? 'cuoi' : ''}>
          <span className="op-tt-cham" aria-hidden="true">{b.so}</span>
          <div className="min-w-0">
            <strong>{b.ten}</strong>
            <small>{b.phu}</small>
          </div>
          <time>{b.tt === 'cho' ? 'chờ' : b.luc ?? '—'}</time>
        </li>
      ))}
    </ol>
  );
}

export function TienTrinhLenh({ chon, duLieu, denCuaTuyen }: {
  chon: ChonTienTrinh;
  /** cabinetId → dữ liệu (`useTienTrinhCacTu`); tủ chưa tải thì vắng */
  duLieu: Map<string, TienTrinhTu>;
  denCuaTuyen: DenBanDo[];
}) {
  const [moDs, setMoDs] = useState(false);
  const tenTheoTu = useMemo(() => {
    const m = new Map<string, Map<number, string>>();
    for (const d of denCuaTuyen) { if (!m.has(d.cabinetId)) m.set(d.cabinetId, new Map()); m.get(d.cabinetId)!.set(d.addr, tenDen(d)); }
    return (tu: string) => (addr: number) => m.get(tu)?.get(addr) ?? `Đèn ${addr.toString(16).toUpperCase()}`;
  }, [denCuaTuyen]);

  /* chọn lệnh theo đối tượng: null = dữ liệu chưa tải; 'khong' = đã tải, không có lệnh dim trong cửa sổ */
  const chonLenh = (): { muc: number | null; mot?: { l: LenhTienTrinh; addr: number; ten: string }; nhieu?: { l: LenhTienTrinh; tenTu: string; ten: (a: number) => string }[] } | null | 'khong' => {
    if (chon.loai === 'den') {
      const tt = duLieu.get(chon.den.cabinetId); if (!tt) return null;
      const l = lenhGanNhatCuaDen(tt.lenh, chon.den.addr);
      return l && l.kind === 'dim' ? { muc: l.level, mot: { l, addr: chon.den.addr, ten: tenDen(chon.den) } } : 'khong';
    }
    const tuDs = chon.loai === 'tu' ? [{ id: chon.id, name: chon.ten }] : chon.tu;
    if (tuDs.some((t) => !duLieu.has(t.id))) return null;
    const moiTu = tuDs.flatMap((t) => { const l = duLieu.get(t.id)!.lenh.find((x) => x.kind === 'dim'); return l ? [{ l, tenTu: t.name, ten: tenTheoTu(t.id) }] : []; });
    if (moiTu.length === 0) return 'khong';
    const moiNhat = moiTu.reduce((a, b) => (b.l.issuedAt > a.l.issuedAt ? b : a));
    const cung = moiTu.filter((x) => new Date(moiNhat.l.issuedAt).getTime() - new Date(x.l.issuedAt).getTime() <= CUNG_LUC_MS);
    if (cung.length === 1 && cung[0].l.dich.length === 1) return { muc: cung[0].l.level, mot: { l: cung[0].l, addr: cung[0].l.dich[0], ten: cung[0].ten(cung[0].l.dich[0]) } };
    return { muc: moiNhat.l.level, nhieu: cung };
  };
  const ch = chonLenh();
  const coCho = ch !== null && ch !== 'khong';
  const now = useBayGio(coCho);
  /* lệnh MỚI xuất hiện (vừa Gửi) ⇒ đưa khối vào tầm nhìn trong thân khung (khung nổi thấp: khối nằm dưới nút Gửi) */
  const goc = useRef<HTMLElement | null>(null);
  const khoaLenh = ch !== null && ch !== 'khong' ? (ch.mot?.l.id ?? ch.nhieu?.map((x) => x.l.id).join(',') ?? '') : '';
  const daThay = useRef<string | null>(null);
  /* chỉ cuộn khi lệnh vừa phát (≤ 30 s) — đổi đối tượng sang một lệnh cũ thì KHÔNG kéo khung đi khỏi nút điều khiển */
  const lucLenh = ch !== null && ch !== 'khong' ? (ch.mot?.l.issuedAt ?? ch.nhieu?.[0]?.l.issuedAt ?? null) : null;
  useEffect(() => {
    if (!khoaLenh) return;
    const moi = lucLenh !== null && Date.now() + (lechGioMs() ?? 0) - new Date(lucLenh).getTime() < 30_000;
    if (daThay.current !== null && daThay.current !== khoaLenh && moi) goc.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    daThay.current = khoaLenh;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [khoaLenh]);
  if (ch === null) return null;
  if (ch === 'khong') return <p className="op-tt-trong" data-o="tien-trinh-trong">Chưa có lệnh dim trong 6 giờ qua</p>;

  const mot = ch.mot ? tienTrinhDen(ch.mot.l, ch.mot.addr, now, ch.mot.ten) : null;
  const nhieu = ch.nhieu ? tienTrinhNhieu(ch.nhieu, now) : null;
  const buoc = mot?.buoc ?? nhieu!.buoc;
  const cho = mot?.choGiay ?? nhieu?.choGiay ?? null;
  const loi = buoc.some((b) => b.tt === 'loi');
  return (
    <section ref={goc} className="op-khoi-tien-trinh" data-o="tien-trinh-lenh" aria-label="Tiến trình lệnh">
      <div className="op-tt-dau">
        <strong>Tiến trình lệnh · {ch.muc ?? '—'} %</strong>
        {cho !== null && <span className="op-tt-dem" data-o="dem-cho">{buoc[1].tt === 'xong' ? 'đang chờ đèn báo' : 'đang chờ host'} · {cho} s</span>}
        {cho === null && !loi && mot?.ket === 'DAT' && <span className="op-tt-dat">{NHAN_KET_QUA.DAT.nhan}</span>}
        {nhieu && cho === null && <span className="op-tt-dat">{nhieu.dat} / {nhieu.tong} đạt</span>}
      </div>
      <DanhSachBuoc buoc={buoc} />
      {nhieu && (
        <div>
          <button type="button" className="op-tt-mo" onClick={() => setMoDs((v) => !v)} aria-expanded={moDs} data-o="mo-ds-den">
            {moDs ? <ChevronDown className="h-4 w-4" aria-hidden="true" /> : <ChevronRight className="h-4 w-4" aria-hidden="true" />}
            {nhieu.dat} / {nhieu.tong} đèn đạt
          </button>
          {moDs && (
            <ul className="op-tt-ds" data-o="ds-den-tien-trinh">
              {nhieu.den.map((d) => (
                <li key={`${d.cabinetLenh}:${d.addr}`} data-ket={d.ket}>
                  <span className="min-w-0 truncate">{d.ten}{nhieu.den.some((x) => x.tenTu !== d.tenTu) ? <small> · {d.tenTu}</small> : null}</span>
                  <span className={`op-tt-nhan op-tt-${NHAN_KET_QUA[d.ket].tone}`}>{NHAN_KET_QUA[d.ket].nhan}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      <p className="op-tt-chu">Báo nhận của host chỉ xác nhận "đã phát ra mesh". Đèn đổi thật hay chưa: xem bước 3 và 4.</p>
    </section>
  );
}
